import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="field-device-info"
// Detects device info from the browser and saves it to the server. Also
// handles the tasker-editable carrier select, which cannot be auto-detected
// (browsers expose no SIM carrier API) and is saved independently.
// Uses navigator.userAgent to extract OS, version, manufacturer, and model.
export default class extends Controller {
  static targets = ["manufacturer", "model", "os", "osVersion", "updatedAt", "button", "status", "carrierSelect"]
  static values  = { url: String, carrierUrl: String }

  // Android's User-Agent Client Hints privacy freeze can report the device
  // model as a generic placeholder (e.g. a bare "K") on modern Android/Chrome
  // -- not a bug in our detection, a documented browser behaviour (SIGNAL's
  // field known-patterns library). Anything this short is untrustworthy and
  // should not silently overwrite a real, previously-saved value.
  static JUNK_MODEL_PATTERN = /^.{1,2}$/

  async save() {
    const info = this._detect()

    if (this._wouldOverwriteWithJunk(info.model)) {
      const currentModel = this.modelTarget.textContent.trim()
      const proceed = confirm(
        `Your browser only reported a generic device model ("${info.model}"). ` +
        `Saving now would overwrite your current saved value ("${currentModel}"). Continue anyway?`
      )
      if (!proceed) return
    }

    this.buttonTarget.disabled = true
    this.statusTarget.textContent = "Saving..."

    const csrfToken = document.querySelector("[name='csrf-token']")?.content
    const response = await fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken
      },
      body: new URLSearchParams({
        device_manufacturer: info.manufacturer,
        device_model:        info.model,
        device_os:           info.os,
        device_os_version:   info.osVersion
      })
    })

    if (response.ok) {
      this.manufacturerTarget.textContent = info.manufacturer || "—"
      this.modelTarget.textContent        = info.model        || "—"
      this.osTarget.textContent           = info.os           || "—"
      this.osVersionTarget.textContent    = info.osVersion    || "—"
      this.updatedAtTarget.textContent    = "Just now"
      this.statusTarget.textContent       = "Saved"
      setTimeout(() => { this.statusTarget.textContent = "" }, 2000)
    } else {
      this.statusTarget.textContent = "Failed to save"
    }

    this.buttonTarget.disabled = false
  }

  async saveCarrier() {
    const carrier = this.carrierSelectTarget.value

    this.statusTarget.textContent = "Saving..."

    const csrfToken = document.querySelector("[name='csrf-token']")?.content
    const response = await fetch(this.carrierUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken
      },
      body: new URLSearchParams({ carrier })
    })

    if (response.ok) {
      this.statusTarget.textContent = "Saved"
      setTimeout(() => { this.statusTarget.textContent = "" }, 2000)
    } else {
      this.statusTarget.textContent = "Failed to save"
    }
  }

  _wouldOverwriteWithJunk(detectedModel) {
    const currentModel = this.modelTarget.textContent.trim()
    const hasRealCurrentValue = currentModel.length > 0 && currentModel !== "—"
    const detectedIsJunk = this.constructor.JUNK_MODEL_PATTERN.test(detectedModel || "")
    return hasRealCurrentValue && detectedIsJunk
  }

  _detect() {
    const ua = navigator.userAgent
    let os = "", osVersion = "", manufacturer = "", model = ""

    // iOS — iPhone / iPad
    if (/iPhone|iPad|iPod/.test(ua)) {
      manufacturer = "Apple"
      model        = /iPad/.test(ua) ? "iPad" : "iPhone"
      const m      = ua.match(/OS (\d+[_\d]*)/)
      osVersion    = m ? m[1].replace(/_/g, ".") : ""
      os           = "iOS"

    // Android
    } else if (/Android/.test(ua)) {
      os = "Android"
      const verMatch = ua.match(/Android ([0-9.]+)/)
      if (verMatch) osVersion = verMatch[1]

      // Extract model from UA — typically "Manufacturer Model Build/..."
      const modelMatch = ua.match(/;\s*([^;)]+?)\s+Build\//) || ua.match(/;\s*([^;)]+)\)/)
      if (modelMatch) {
        const raw    = modelMatch[1].trim()
        const brands = ["Samsung", "Google", "Huawei", "Xiaomi", "OnePlus", "OPPO",
                        "Vivo", "Realme", "Motorola", "Nokia", "Sony", "LG", "Pixel"]
        const brand  = brands.find(b => raw.toLowerCase().startsWith(b.toLowerCase()))
        if (brand) {
          manufacturer = brand
          model        = raw.slice(brand.length).trim() || raw
        } else {
          model        = raw
          manufacturer = raw.split(" ")[0]
        }
      }

    // macOS
    } else if (/Mac OS X/.test(ua)) {
      os           = "macOS"
      manufacturer = "Apple"
      const m      = ua.match(/Mac OS X ([0-9_]+)/)
      osVersion    = m ? m[1].replace(/_/g, ".") : ""

    // Windows
    } else if (/Windows/.test(ua)) {
      os = "Windows"
      const m = ua.match(/Windows NT ([0-9.]+)/)
      if (m) {
        const ntMap = { "10.0": "10 / 11", "6.3": "8.1", "6.2": "8", "6.1": "7" }
        osVersion = ntMap[m[1]] || m[1]
      }

    // Linux / ChromeOS
    } else if (/CrOS/.test(ua)) {
      os = "ChromeOS"
    } else if (/Linux/.test(ua)) {
      os = "Linux"
    }

    return { os, osVersion, manufacturer, model }
  }
}
